import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import { queryKeys } from '../../../shared/api/query-keys';

export type TaxConditionOperator = 'EQ' | 'NEQ' | 'IN' | 'STARTS_WITH' | 'RANGE' | 'OR' | 'AND';

export interface TaxCondition {
  field: string;
  operator: TaxConditionOperator;
  value: string;
}

/** Espelha `TaxAction` do motor de tributo. */
export interface TaxRuleAction {
  taxType: string;
  cst: string;
  rateType: string;
  mva?: number;
  baseReduction?: number;
  apply: boolean;
}

/** Espelha `TaxRuleResponse` do controller. */
export interface TaxRule {
  id: string;
  name: string;
  description: string | null;
  priority: number;
  enabled: boolean;
  conditions: TaxCondition[];
  actions: TaxRuleAction[];
  effectiveSince: string;
  effectiveUntil: string | null;
}

export interface TaxRuleInput {
  name: string;
  description?: string;
  priority: number;
  enabled?: boolean;
  conditions: TaxCondition[];
  actions: TaxRuleAction[];
  /* Vigencia e obrigatoria na criacao: regra fiscal sem data de inicio nao
     tem como ser aplicada a um documento. */
  effectiveSince: string;
  effectiveUntil?: string | null;
}

export function useTaxRules() {
  return useQuery({
    queryKey: queryKeys.fiscal.taxRules(),
    queryFn: () => apiClient.get<TaxRule[]>('/fiscal/tax-rules'),
  });
}

export function useCreateTaxRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxRuleInput) => apiClient.post<TaxRule>('/fiscal/tax-rules', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRules() }),
  });
}

export function useUpdateTaxRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: TaxRuleInput & { id: string }) =>
      apiClient.put<TaxRule>(`/fiscal/tax-rules/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRules() }),
  });
}

export function useDeleteTaxRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/tax-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.fiscal.taxRules() }),
  });
}
